/**
 * Pure client-side validation for the Sponsor a Prize onboarding form.
 * Kept framework-free so it can be unit-tested in isolation.
 */
import type { ApplicationPayload } from './api';

export type FormValues = {
  company_name: string;
  company_reg_no: string;
  industry: string;
  website: string;
  contact_name: string;
  email: string;
  phone: string;
  prize_product: string;
  prize_value: string;
  sponsorship_type: ApplicationPayload['sponsorship_type'] | '';
  campaign_objective: string;
  target_audience: string;
  preferred_dates: string;
  qr_placement: ApplicationPayload['qr_placement'] | '';
  wants_leads: boolean;
  wants_ai_marketing: boolean;
  consent_terms: boolean;
  image_urls: string;
  notes: string;
  company_url: string; // honeypot
};

export const EMPTY_FORM: FormValues = {
  company_name: '',
  company_reg_no: '',
  industry: '',
  website: '',
  contact_name: '',
  email: '',
  phone: '',
  prize_product: '',
  prize_value: '',
  sponsorship_type: '',
  campaign_objective: '',
  target_audience: '',
  preferred_dates: '',
  qr_placement: '',
  wants_leads: false,
  wants_ai_marketing: false,
  consent_terms: false,
  image_urls: '',
  notes: '',
  company_url: '',
};

export type FieldErrors = Partial<Record<keyof FormValues, string>>;

// Fields grouped by wizard step — used both for rendering and per-step validation.
export const STEP_FIELDS: (keyof FormValues)[][] = [
  // Step 1 — Company
  ['company_name', 'company_reg_no', 'industry', 'website'],
  // Step 2 — Contact
  ['contact_name', 'email', 'phone'],
  // Step 3 — Prize & campaign
  ['prize_product', 'prize_value', 'sponsorship_type', 'campaign_objective', 'target_audience', 'preferred_dates'],
  // Step 4 — Tracking, opt-ins & consent
  ['qr_placement', 'image_urls', 'notes', 'consent_terms'],
];

const URL_RE = /^https?:\/\/[^\s.]+\.[^\s]{2,}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+]?[\d\s()-]{7,20}$/;

export function validateField(name: keyof FormValues, values: FormValues): string | undefined {
  const v = values[name];
  const str = typeof v === 'string' ? v.trim() : v;

  switch (name) {
    case 'company_name':
      if (!str) return 'Company name is required.';
      return undefined;
    case 'industry':
      if (!str) return 'Industry is required.';
      return undefined;
    case 'website':
      if (!str) return 'Website is required.';
      if (!URL_RE.test(String(str))) return 'Enter a valid URL (e.g. https://example.com).';
      return undefined;
    case 'contact_name':
      if (!str) return 'Contact person is required.';
      return undefined;
    case 'email':
      if (!str) return 'Email address is required.';
      if (!EMAIL_RE.test(String(str))) return 'Enter a valid email address.';
      return undefined;
    case 'phone':
      if (!str) return 'Phone number is required.';
      if (!PHONE_RE.test(String(str))) return 'Enter a valid phone number.';
      return undefined;
    case 'prize_product':
      if (!str) return 'Tell us the prize or product you want to sponsor.';
      return undefined;
    case 'prize_value':
      if (!str) return 'Prize value is required.';
      return undefined;
    case 'sponsorship_type':
      if (!str) return 'Select whether the prize is fully sponsored, discounted or co-funded.';
      return undefined;
    case 'campaign_objective':
      if (!str) return 'Campaign objective is required.';
      return undefined;
    case 'target_audience':
      if (!str) return 'Target audience is required.';
      return undefined;
    case 'preferred_dates':
      if (!str) return 'Preferred campaign dates are required.';
      return undefined;
    case 'qr_placement':
      if (!str) return 'Tell us where QR codes will be used.';
      return undefined;
    case 'consent_terms':
      if (v !== true) return 'Please confirm you have authority to submit this application.';
      return undefined;
    default:
      return undefined;
  }
}

export function validateStep(stepIndex: number, values: FormValues): FieldErrors {
  const errors: FieldErrors = {};
  for (const field of STEP_FIELDS[stepIndex] ?? []) {
    const err = validateField(field, values);
    if (err) errors[field] = err;
  }
  return errors;
}

export function validateAll(values: FormValues): FieldErrors {
  const errors: FieldErrors = {};
  STEP_FIELDS.forEach((_, i) => Object.assign(errors, validateStep(i, values)));
  return errors;
}

/** Build the API payload from validated form values. */
export function toPayload(values: FormValues): ApplicationPayload {
  const imageUrls = values.image_urls
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

  return {
    company_name: values.company_name.trim(),
    company_reg_no: values.company_reg_no.trim() || undefined,
    industry: values.industry.trim(),
    website: values.website.trim(),
    contact_name: values.contact_name.trim(),
    email: values.email.trim(),
    phone: values.phone.trim(),
    prize_product: values.prize_product.trim(),
    prize_value: values.prize_value.trim(),
    sponsorship_type: values.sponsorship_type as ApplicationPayload['sponsorship_type'],
    campaign_objective: values.campaign_objective.trim(),
    target_audience: values.target_audience.trim(),
    preferred_dates: values.preferred_dates.trim(),
    qr_placement: values.qr_placement as ApplicationPayload['qr_placement'],
    wants_leads: values.wants_leads,
    wants_ai_marketing: values.wants_ai_marketing,
    image_urls: imageUrls.length ? imageUrls : undefined,
    notes: values.notes.trim() || undefined,
    consent_acknowledged: values.consent_terms,
    company_url: values.company_url, // honeypot, should be ''
  };
}
